import { defineMessages } from 'react-intl';

const messages = defineMessages({
  historicalData: {
    id: 'Historical data',
    defaultMessage: 'Dati storici',
  },
  datasetCode: {
    id: 'Dataset code',
    defaultMessage: 'Codice dataset',
  },
  fromDateProp: {
    id: 'From date property',
    defaultMessage: 'Proprietà data "dal"',
  },
  toDateProp: {
    id: 'To date property',
    defaultMessage: 'Proprietà data "al"',
  },
  requiredFilters: {
    id: 'Required filters',
    defaultMessage: 'Filtri obbligatori',
  },
  minDate: {
    id: 'Minimum date',
    defaultMessage: 'Data minima',
  },
  maxDate: {
    id: 'Maximum date',
    defaultMessage: 'Data massima',
  },
  maxDays: {
    id: 'Maximum range (days)',
    defaultMessage: 'Range massimo (gg)',
  },
  mandatory: {
    id: 'Mandatory',
    defaultMessage: 'Obbligatorio',
  },
  selectList: {
    id: 'Select from list',
    defaultMessage: 'Seleziona dal lista',
  },
  source: {
    id: 'Source',
    defaultMessage: 'Sorgente',
  },
  sourcepropVal: {
    id: 'Source prop val',
    defaultMessage: 'Sorgente prop val',
  },
  sourcepropLab: {
    id: 'Source prop lab',
    defaultMessage: 'Sorgente prop lab',
  },
  filters: {
    id: 'Filters',
    defaultMessage: 'Filtri',
  },
  filterBy: {
    id: 'Filter by',
    defaultMessage: 'Filtra per',
  },
  filterProp: {
    id: 'Filter property',
    defaultMessage: 'Filtra proprietà',
  },
  labelField: {
    id: 'label',
    defaultMessage: 'Label',
  },
  property: {
    id: 'Property',
    defaultMessage: 'Proprietà',
  },
  filterType: {
    id: 'Filter type',
    defaultMessage: 'Tipo',
  },
  selectText: {
    id: 'Text',
    defaultMessage: 'Testo',
  },
  selectNumber: {
    id: 'Number',
    defaultMessage: 'Numero',
  },
  values: {
    id: 'values',
    defaultMessage: 'Valori',
  },
  valuesDesc: {
    id: 'Enter options, one per row',
    defaultMessage: 'Inserire le opzioni, una per riga',
  },
});

const HistoricalData = ({ intl }) => ({
  title: intl.formatMessage(messages.historicalData),
  fieldsets: [
    {
      id: 'default',
      title: intl.formatMessage({ id: 'Default' }),
      fields: [
        'datasetCode',
        'fromDateProp',
        'toDateProp',
        'requiredFilters',
        'minDate',
        'maxDate',
        'maxDays',
        'filters',
      ],
    },
  ],
  properties: {
    datasetCode: {
      title: `${intl.formatMessage(messages.datasetCode)}`,
    },
    fromDateProp: {
      title: intl.formatMessage(messages.fromDateProp),
    },
    toDateProp: {
      title: intl.formatMessage(messages.toDateProp),
    },
    requiredFilters: {
      factory: 'array',
      type: 'array',
      title: intl.formatMessage(messages.requiredFilters),
      items: {
        choices: [
          ['labelField', intl.formatMessage(messages.labelField)],
          ['propertyField', intl.formatMessage(messages.property)],
          ['mandatory', intl.formatMessage(messages.mandatory)],
          ['filterType', intl.formatMessage(messages.filterType)],
          ['provinceValues', intl.formatMessage(messages.values)],
          ['source', intl.formatMessage(messages.source)],
          ['sourcepropVal', intl.formatMessage(messages.sourcepropVal)],
          ['sourcepropLab', intl.formatMessage(messages.sourcepropLab)],
          ['filterBy', intl.formatMessage(messages.filterBy)],
          ['filterProp', intl.formatMessage(messages.filterProp)],
        ],
        type: 'string',
      },
    },
    minDate: {
      title: intl.formatMessage(messages.minDate),
      type: 'date',
    },
    maxDate: {
      title: intl.formatMessage(messages.maxDate),
      type: 'date',
    },
    maxDays: {
      title: intl.formatMessage(messages.maxDays),
      minimum: 0,
      type: 'integer',
    },
    filters: {
      title: intl.formatMessage(messages.filters),
      widget: 'object_list_inline',
      schema: FiltersSchema({ intl }),
    },
  },
  required: ['filters'],
});

const FiltersSchema = ({ intl, formData }) => {
  const filterType = formData?.filterType || 'selectList';

  const baseProperties = {
    labelField: {
      title: intl.formatMessage(messages.labelField),
    },
    propertyField: {
      title: intl.formatMessage(messages.property),
    },
    mandatory: {
      type: 'boolean',
      title: intl.formatMessage(messages.mandatory),
    },
    filterType: {
      factory: 'Choice',
      type: 'string',
      title: intl.formatMessage(messages.filterType),
      default: intl.formatMessage(messages.selectList),
      choices: [
        ['selectList', intl.formatMessage(messages.selectList)],
        ['source', intl.formatMessage(messages.source)],
        ['text', intl.formatMessage(messages.selectText)],
        ['number', intl.formatMessage(messages.selectNumber)],
      ],
    },
    provinceValues: {
      title: intl.formatMessage(messages.values),
      description: intl.formatMessage(messages.valuesDesc),
      widget: 'textarea',
    },
    source: {
      title: intl.formatMessage(messages.source),
    },
    sourcepropVal: {
      title: intl.formatMessage(messages.sourcepropVal),
    },
    sourcepropLab: {
      title: intl.formatMessage(messages.sourcepropLab),
    },
    filterBy: {
      title: intl.formatMessage(messages.filterBy),
    },
    filterProp: {
      title: intl.formatMessage(messages.filterProp),
    },
  };

  // Conditionally add fields based on filterType
  const properties = { ...baseProperties };

  if (filterType === 'selectList') {
    properties.provinceValues = {
      title: intl.formatMessage(messages.values),
      description: intl.formatMessage(messages.valuesDesc),
      widget: 'textarea',
    };
  }

  if (filterType === 'source') {
    properties.source = {
      title: intl.formatMessage(messages.source),
    };
    properties.sourcepropVal = {
      title: intl.formatMessage(messages.sourcepropVal),
    };
    properties.sourcepropLab = {
      title: intl.formatMessage(messages.sourcepropLab),
    };
    properties.filterBy = {
      title: intl.formatMessage(messages.filterBy),
    };
    properties.filterProp = {
      title: intl.formatMessage(messages.filterProp),
    };
  }

  return {
    title: intl.formatMessage(messages.filters),
    fieldsets: [
      {
        id: 'filters',
        title: intl.formatMessage({ id: 'filters' }),
        fields: Object.keys(properties),
      },
    ],
    properties,
    required: [],
  };
};

export default HistoricalData;
